import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { getInspectionById } from "src/store/inspectionSlice";
import { setInspectionResults } from "src/store/inspectionResultsSlice";

interface UseResultPollingProps {
  enabled: boolean;
  inspectionId: string;
  questionId: string | null;
  onResultReady?: (result: any) => void;
}

/**
 * Custom hook to poll for inspection result updates
 * Used when waiting for async analysis to complete
 */
export const useResultPolling = ({
  enabled,
  inspectionId,
  questionId,
  onResultReady,
}: UseResultPollingProps) => {
  const dispatch = useAppDispatch();
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!enabled || !inspectionId || !questionId) {
      return;
    }

    // Start polling
    pollIntervalRef.current = setInterval(async () => {
      try {
        const result = await dispatch(getInspectionById(inspectionId)).unwrap();
        
        // Update results in Redux
        dispatch(setInspectionResults(result.results));
        
        // Check if the specific question result is ready
        const questionResult = result.results.find(
          (r: any) => r.questionId === questionId
        );
        
        if (questionResult && questionResult.aiResult !== "PENDING") {
          // Result is ready - stop polling and notify
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          
          if (onResultReady) {
            onResultReady(questionResult);
          }
        }
      } catch (error) {
        console.error("Polling error:", error);
      }
    }, 2000);

    // Cleanup on unmount or when disabled
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [enabled, inspectionId, questionId, dispatch, onResultReady]);
};
